import type { LearningExercise, LearningPathId, ReviewStatus, SourceRef } from "../types";

const levelLabels = ["Bekannt", "Abrufbar", "Anwendbar", "Pruefungsstabil", "Unter Zeitdruck stabil"];

type SourceMap = Record<string, SourceRef>;

interface BaseSpec {
  id: string;
  pathId: LearningPathId;
  topic: string;
  title: string;
  prompt: string;
  explanation: string;
  sourceIds: string[];
  levelNumber?: number;
  errorCategory?: string;
  skillTags?: string[];
  reviewStatus?: ReviewStatus;
}

function base(sourceMap: SourceMap, spec: BaseSpec): Omit<LearningExercise, "kind" | "options" | "answers"> {
  const levelNumber = spec.levelNumber ?? 2;
  return {
    id: spec.id,
    pathId: spec.pathId,
    mode: "ausbildung",
    topic: spec.topic,
    title: spec.title,
    prompt: spec.prompt,
    explanation: spec.explanation,
    sourceIds: spec.sourceIds,
    sources: spec.sourceIds.map((id) => sourceMap[id]).filter(Boolean),
    level: levelLabels[levelNumber - 1] ?? "Abrufbar",
    levelNumber,
    errorCategory: spec.errorCategory ?? "Definition",
    skillTags: spec.skillTags ?? ["grundlagen"],
    reviewStatus: spec.reviewStatus ?? "draft",
  };
}

export function makeSingle(
  sourceMap: SourceMap,
  spec: BaseSpec & { options: [string, string, string, string]; answer: number }
): LearningExercise {
  return { ...base(sourceMap, spec), kind: "single", options: spec.options, answers: [spec.answer] };
}

export function makeMulti(
  sourceMap: SourceMap,
  spec: BaseSpec & { options: [string, string, string, string]; answers: [number, number] | [number, number, number] }
): LearningExercise {
  return { ...base(sourceMap, spec), kind: "multi", options: spec.options, answers: spec.answers };
}

export function makeFlashcard(sourceMap: SourceMap, spec: BaseSpec): LearningExercise {
  return { ...base(sourceMap, spec), kind: "flashcard", options: [], answers: [] };
}

export function makeOrdering(sourceMap: SourceMap, spec: BaseSpec & { orderItems: string[] }): LearningExercise {
  return { ...base(sourceMap, spec), kind: "ordering", options: [], answers: [], orderItems: spec.orderItems };
}

export function makeCloze(
  sourceMap: SourceMap,
  spec: BaseSpec & { options: [string, string, string, string]; answer: number }
): LearningExercise {
  return { ...base(sourceMap, spec), kind: "cloze", options: spec.options, answers: [spec.answer] };
}
